/**
 * Font-family canonicalization + Morisawa webfont resolution.
 *
 * normalizeFontFamily and resolveFontFamily are deliberately split:
 *  - normalize rewrites the legacy English DB-default family name
 *    'UD Digi Kyokasho' to the canonical logical name 'UDデジタル教科書体'.
 *    The canonical name is what SettingsPanel's <select> options and the
 *    persisted settings value are keyed on, so ONLY the canonical form may
 *    reach component state / the save API (never a legacy or MFW name).
 *  - resolve is render-time only: it inserts the real Morisawa ProN Regular
 *    family in front of the UD token so the webfont (when loaded via
 *    REACT_APP_MORISAWA_WEBFONT_URL) actually wins. Insertion is harmless when
 *    the webfont is NOT loaded — an unknown family is skipped per glyph — which
 *    is why it is unconditional. The MFW name must never be persisted, so
 *    resolve is applied at style-application sites, not to stored state.
 */

// Bare CJK family name shared by the @font-face alias and every stack literal.
const UD_TOKEN = 'UDデジタル教科書体';
// Legacy English family = the backend User.font_family DB default; existing
// users carry it in saved settings. Case-insensitive: quotes are outside the
// match, so 'UD Digi Kyokasho' / "UD Digi Kyokasho" / bare all canonicalize.
const LEGACY_UD_PATTERN = /UD Digi Kyokasho/gi;
// Real Morisawa-delivered family (ProN Regular). CSS family matching is
// case-insensitive, so this still matches the lowercase @font-face in mf.css.
const MFW_REGULAR_TOKEN = "'MFW-UDDigiKyokashoProN-Regular'";

export function normalizeFontFamily(value) {
  if (typeof value !== 'string') {
    return value;
  }
  return value.replace(LEGACY_UD_PATTERN, UD_TOKEN);
}

export function resolveFontFamily(value) {
  const normalized = normalizeFontFamily(value);
  if (typeof normalized !== 'string') {
    return normalized;
  }
  // Only UD stacks need the webfont, and only once (idempotent): a value that
  // already carries an 'MFW-' token is returned untouched.
  if (!normalized.includes(UD_TOKEN) || normalized.includes('MFW-')) {
    return normalized;
  }
  // Insert immediately before the FIRST UD token, keeping any higher-priority
  // families (e.g. YuKyokasho) ahead of it — never a blind prepend. A leading
  // quote is pulled into the tail so the MFW family lands outside UD's quotes.
  const udIndex = normalized.indexOf(UD_TOKEN);
  const precedingChar = normalized[udIndex - 1];
  const insertAt = (precedingChar === "'" || precedingChar === '"') ? udIndex - 1 : udIndex;
  return normalized.slice(0, insertAt) + MFW_REGULAR_TOKEN + ', ' + normalized.slice(insertAt);
}
